<template>
  <div class="sizes-editor">
    <div class="sizes-editor__toolbar">
      <v-switch
        v-model="includesSizes"
        inset
        label="Has sizes"
        hide-details
        @change="onSizesChange"
      ></v-switch>

      <v-btn type="button" color="primary" depressed :disabled="!includesSizes" @click="addEmptySize()">
        <v-icon left small>mdi-plus</v-icon>
        Add size
      </v-btn>
    </div>

    <div v-if="includesSizes" class="size-list">
      <v-card v-for="(size, index) in sizes" :key="index" class="size-card" outlined>
        <div class="size-card__header">
          <div>
            <span>Size option</span>
            <strong>{{ sizeLabel(size, index) }}</strong>
          </div>

          <v-btn icon color="error" aria-label="Remove size" @click="remove(size)">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </div>

        <div class="size-card__grid">
          <v-text-field v-model="size.size" label="Size" outlined dense hide-details="auto" />
          <v-text-field
            v-model="size.surplus"
            label="Price"
            type="number"
            inputmode="decimal"
            prefix="GBP"
            outlined
            dense
            hide-details="auto"
          />
          <v-text-field
            v-model="size.extraSurplus"
            label="Extra Surplus"
            type="number"
            inputmode="decimal"
            prefix="GBP"
            outlined
            dense
            hide-details="auto"
          />
          <v-text-field
            v-model="size.instoreSurplus"
            label="Instore"
            type="number"
            inputmode="decimal"
            prefix="GBP"
            outlined
            dense
            hide-details="auto"
          />
          <v-text-field
            v-if="isVolume"
            v-model="size.volumeConsumedMl"
            label="Volume per serving (ml)"
            type="number"
            inputmode="decimal"
            suffix="ml"
            outlined
            dense
            hide-details="auto"
            hint="e.g. 25 single, 50 double"
            persistent-hint
          />
        </div>

        <div class="size-card__subheader">
          <span>Nested options</span>
          <v-btn type="button" text small color="primary" @click="addAdditionalEmptySize(size)">
            <v-icon left small>mdi-plus</v-icon>
            Add option
          </v-btn>
        </div>

        <div v-if="size.sizes != null && size.sizes.length > 0" class="nested-size-list">
          <div v-for="(additionalSize, sizeIndex) in size.sizes" :key="sizeIndex" class="nested-size">
            <v-text-field
              v-model="additionalSize.option"
              label="Option"
              outlined
              dense
              hide-details="auto"
            />
            <v-text-field
              v-model="additionalSize.surplus"
              label="Surplus"
              type="number"
              inputmode="decimal"
              prefix="GBP"
              outlined
              dense
              hide-details="auto"
            />
            <v-btn icon color="error" aria-label="Remove option" @click="removeAdditionalSize(size, additionalSize)">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </div>
        </div>
      </v-card>
    </div>

    <div v-else class="sizes-editor__empty">
      No size variants enabled.
    </div>
  </div>
</template>

<script>
export default {
  props: ['productSizes', 'isVolume'],
  data () {
    return {
      sizes: [],
      includesSizes: false
    }
  },
  methods: {
    onSizesChange: function(enabled) {
      this.sizes = [];
      if (enabled) {
        this.addEmptySize();
      }
    },
    getSizes: function() {
      return this.includesSizes ? this.sizes : [];
    },
    hasSizes: function() {
      return this.includesSizes && this.sizes.length > 0;
    },
    sizeLabel: function(size, index) {
      return size.size || 'Size ' + (index + 1);
    },
    addEmptySize: function() {
      this.sizes.push({
        size: '',
        surplus: null,
        extraSurplus: 0,
        instoreSurplus: 0,
        volumeConsumedMl: 0,
        sizes: []
      })
    },

    remove: function(size) {
      var idx = this.sizes.indexOf(size);
      if (idx > -1) {
        this.sizes.splice(idx, 1);
      }
    },

    addAdditionalEmptySize: function(size) {
      if (size.sizes == null) { size.sizes = []; }

      size.sizes.push({
        option: '',
        surplus: null
      })

      this.$forceUpdate();
    },

    removeAdditionalSize: function(size, additionalSize) {
      var idx = size.sizes.indexOf(additionalSize);
      if (idx > -1) {
        size.sizes.splice(idx, 1);
      }

      this.$forceUpdate();
    }
  },
  watch: {
    productSizes: {
      immediate: true,
      handler(val) {
        if (val && val.length > 0) {
          this.includesSizes = true;
          var sizes = JSON.parse(JSON.stringify(this.productSizes));
          this.sizes = sizes.sort((a, b) => a.surplus - b.surplus);
          for (let size of this.sizes) {
            if (size.sizes != null)
              size.sizes = size.sizes.sort((a, b) => a.surplus - b.surplus);
          }
        } else {
          this.includesSizes = false;
          this.sizes = [];
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.sizes-editor {
  display: grid;
  gap: 12px;
}

.sizes-editor__toolbar {
  align-items: stretch;
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr;

  ::v-deep .v-input--selection-controls {
    margin-top: 0;
    padding-top: 0;
  }

  ::v-deep .v-input__slot {
    align-items: center;
    margin-bottom: 0;
  }
}

.size-list {
  display: grid;
  gap: 12px;
}

.size-card {
  padding: 14px;
}

.size-card__header,
.size-card__subheader {
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: space-between;
}

.size-card__header {
  margin-bottom: 12px;

  span {
    color: #64748b;
    display: block;
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
  }

  strong {
    color: #111827;
    display: block;
    font-size: 17px;
    margin-top: 2px;
  }
}

.size-card__grid,
.nested-size {
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr;
}

.size-card__subheader {
  border-top: 1px solid #dfe7ef;
  color: #64748b;
  font-size: 12px;
  font-weight: 800;
  margin-top: 14px;
  padding-top: 12px;
  text-transform: uppercase;
}

.nested-size-list {
  display: grid;
  gap: 10px;
  margin-top: 10px;
}

.nested-size {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #dfe7ef;
  border-radius: 8px;
  padding: 10px;
}

.sizes-editor__empty {
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  color: #64748b;
  padding: 18px;
  text-align: center;
}

@media (min-width: 720px) {
  .sizes-editor__toolbar {
    align-items: center;
    grid-template-columns: 1fr auto;
  }

  .size-card__grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .nested-size {
    grid-template-columns: minmax(0, 1fr) minmax(160px, 220px) auto;
  }
}
</style>
